import React, { useState, useEffect, useCallback, useContext } from 'react';
import { UserContext } from "../UserContext";
import { useNavigate } from "react-router-dom";
import Footer from "../Footer";
import AdminNav from "../Navbars/AdminNav";
import axios from 'axios';
import './ChatbotAdminPanel.css';
import { Link } from 'react-router-dom';

// Fetch the API Base URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API;

function ChatbotAdminPanel() {
  const [view, setView] = useState('questions'); // Manage the current view (questions, responses, options)
  const [items, setItems] = useState([]); // Store the fetched items for the current view
  const [newItem, setNewItem] = useState({}); // New item data for adding
  const [editItemId, setEditItemId] = useState(null); // ID of the item currently being edited
  const [editFormData, setEditFormData] = useState({}); // Form data for editing an item
  const [alert, setAlert] = useState({ type: '', message: '', visible: false }); // Status alerts

  // User context and navigation
  const { isAdmin, setUserRole, setUserInfo, userInfo } = useContext(UserContext);
  const navigate = useNavigate();

  // Fetch user info on component mount
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/user`, {
          credentials: "include",
        });
        const data = await response.json();
        setUserInfo(data);
        if (data?.usertype) {
          setUserRole(data.usertype);
        }
      } catch (error) {
        console.log("Error in fetchUserInfo:", error);
      }
    };

    fetchUserInfo();
  }, [setUserInfo, setUserRole]);

  // Fetch items from the server for the selected view
  const fetchItems = useCallback(async () => {
    try {
      const endpoint = `${API_BASE_URL}/api/chatbot-admin/${view}`;
      const response = await axios.get(endpoint);
      setItems(response.data);
    } catch (error) {
      console.error(`Error fetching ${view}`, error);
      showAlert('error', `Failed to fetch ${view}.`);
    }
  }, [view]);

  // Fetch items whenever the selected view changes
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Show an alert with a type (success/error) and message
  const showAlert = (type, message) => {
    setAlert({ type, message, visible: true });
    setTimeout(() => setAlert({ type: '', message: '', visible: false }), 3000);
  };

  // Handle logout
  const onLogout = () => {
    fetch(`${API_BASE_URL}/logout`, {
      credentials: "include",
      method: "POST",
    })
      .then(() => {
        navigate("/");
      })
      .catch((error) => {
        console.error("Logout failed:", error);
      });
    setUserInfo(null);
    setUserRole("guest");
  };

  // Handle deleting an item
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/chatbot-admin/${view}/${id}`);
      fetchItems();
      showAlert('success', `Selected ${view.slice(0, -1)} deleted successfully.`);
    } catch (error) {
      console.error(`Error deleting ${view} with id ${id}`, error);
      showAlert('error', `Failed to delete ${view.slice(0, -1)}.`);
    }
  };

  // Handle editing an item
  const handleEditClick = (item) => {
    setEditItemId(item.id);
    setEditFormData({ ...item });
  };

  // Handle input changes for the edit form
  const handleEditInputChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Save updates to the server
  const handleUpdateSave = async (id) => {
    try {
      await axios.put(`${API_BASE_URL}/api/chatbot-admin/${view}/${id}`, editFormData);
      setEditItemId(null);
      fetchItems();
      showAlert('success', `Selected ${view.slice(0, -1)} updated successfully.`);
    } catch (error) {
      console.error(`Error updating ${view} with id ${id}`, error);
      showAlert('error', `Failed to update ${view.slice(0, -1)}.`);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditItemId(null);
  };

  // Handle input changes for the new item form
  const handleInputChange = (field, value) => {
    setNewItem((prev) => ({ ...prev, [field]: value }));
  };

  // Add a new item to the server
  const handleAdd = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/chatbot-admin/${view}`, newItem);
      setNewItem({});
      fetchItems();
      showAlert('success', `New ${view.slice(0, -1)} added successfully.`);
    } catch (error) {
      console.error(`Error adding new ${view}`, error);
      showAlert('error', `Failed to add new ${view.slice(0, -1)}.`);
    }
  };

  // Define form fields for each view
  const formFields = {
    questions: [
      { name: 'question_text', label: 'Question Text' },
      { name: 'first_question_for', label: 'First Question For (Page Identifier)' },
    ],
    responses: [
      { name: 'response_text', label: 'Response Text' },
      { name: 'document_link', label: 'Document Link' },
    ],
    options: [
      { name: 'option_text', label: 'Option Text' },
      { name: 'question_id', label: 'Question ID' },
      { name: 'next_question_id', label: 'Next Question ID' },
      { name: 'response_id', label: 'Response ID' },
    ],
  };

  const username = userInfo?.username;

  return (
    <>
      {username && isAdmin && <AdminNav username={username} onLogout={onLogout} />}

      <div className="chatbot-admin-panel">
        {/* Status Alert */}
        {alert.visible && (
          <div className={`alert ${alert.type}`}>
            {alert.message}
          </div>
        )}

        <div className="admin-panel-header">
          <h2 className="page-title">Chatbot Admin Panel</h2>

          {/* Toggle buttons to switch views */}
          <div className="admin-panel-toggle">
            <button onClick={() => setView('questions')}>Questions</button>
            <button onClick={() => setView('responses')}>Responses</button>
            <button onClick={() => setView('options')}>Options</button>
          </div>
        </div>

        {/* Content Area */}
        <div className="admin-panel-content">
          {items.map((item) => (
            <div key={item.id} className="admin-panel-item">
              {editItemId === item.id ? (
                <div className="admin-panel-edit-form">
                  {formFields[view].map((field) => (
                    <div key={field.name}>
                      <label>{field.label}</label>
                      <input
                        type="text"
                        value={editFormData[field.name] || ''}
                        onChange={(e) => handleEditInputChange(field.name, e.target.value)}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                      />
                    </div>
                  ))}
                  <button className="save-button" onClick={() => handleUpdateSave(item.id)}>Save</button>
                  <button className="cancel-button" onClick={handleCancelEdit}>Cancel</button>
                </div>
              ) : (
                <div>
                  {/* Display fields for each view */}
                  {view === 'questions' && (
                    <div>
                      <strong>Question ID:</strong> {item.id} <br /><br />
                      <strong>Question Text:</strong> {item.question_text} <br />
                      <strong>First Question for:</strong> {item.first_question_for || 'None'}
                    </div>
                  )}
                  {view === 'responses' && (
                    <div>
                      <strong>Response ID:</strong> {item.id} <br /><br />
                      <strong>Response Text:</strong> {item.response_text} <br />
                      <strong>Document Link:</strong>
                      {item.document_link ? (
                        <a href={item.document_link} target="_blank" rel="noopener noreferrer">Download</a>
                      ) : (
                        'None'
                      )}
                    </div>
                  )}
                  {view === 'options' && (
                    <div>
                      <strong>Option ID:</strong> {item.id} <br /><br />
                      <strong>Option Text:</strong> {item.option_text} <br />
                      <strong>Question ID:</strong> {item.question_id} <br />
                      <strong>Next Question ID:</strong> {item.next_question_id || 'None'} <br />
                      <strong>Response ID:</strong> {item.response_id || 'None'}
                    </div>
                  )}
                  <div className="admin-panel-item-buttons">
                    <button onClick={() => handleDelete(item.id)}>Delete</button>
                    <button onClick={() => handleEditClick(item)}>Update</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Form for adding new items */}
        <div className="admin-panel-add">
          <h4>Add New {view.slice(0, -1)}</h4>
          {formFields[view]?.map((field) => (
            <div key={field.name}>
              <label>{field.label}</label>
              <input
                type="text"
                value={newItem[field.name] || ''}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                placeholder={`Enter ${field.label.toLowerCase()}`}
              />
            </div>
          ))}
          <button onClick={handleAdd}>Add</button>

        </div>
        <Link to="/BookADemo">
          <button className="redeem-buttons">
            Book a Demo
          </button>
        </Link>
      </div>

      <Footer />
    </>
  );
}

export default ChatbotAdminPanel;